/**
 * Camlin Companion — real dwell/scroll/exit-intent trigger detectors (doc
 * 116 §2.4, CO2).
 *
 * `proactive.ts` (CO0) already implements the pure cap/dismiss evaluator
 * (`evaluateProactiveNudge`) and the pure trigger-vs-context matcher
 * (`matchProactiveTrigger`) — this module is what PRODUCES the
 * `{ pagePath, dwellSeconds, scrollFraction }` context from real DOM
 * signals and calls `onTriggerMatched` the first time a configured trigger
 * fires. It never decides whether a nudge is ALLOWED to show — that stays
 * `evaluateProactiveNudge`'s (client cap check) and the server's
 * (`engagementClient.ts`) job, both called by the caller of this watcher.
 */

import { matchProactiveTrigger } from './proactive';
import type { CompanionProactiveTrigger } from './types';

/** Fraction of the page scrolled, clamped to [0, 1]. Returns 0 (not NaN) when the page is no taller than the viewport. */
export function computeScrollFraction(
  scrollTop: number,
  viewportHeight: number,
  documentHeight: number
): number {
  const scrollableHeight = documentHeight - viewportHeight;
  if (scrollableHeight <= 0) {
    return 0;
  }
  const fraction = scrollTop / scrollableHeight;
  return Math.min(1, Math.max(0, fraction));
}

/** Standard "exit intent" heuristic: the cursor left through the very top edge of the viewport, heading for the tab/URL bar. */
export function isExitIntentClientY(clientY: number): boolean {
  return clientY <= 0;
}

export interface ProactiveTriggerWatcherOptions {
  triggers: CompanionProactiveTrigger[];
  pagePath: string;
  onTriggerMatched: (trigger: CompanionProactiveTrigger) => void;
  win?: Window;
  doc?: Document;
}

export interface ProactiveTriggerWatcherHandle {
  stop: () => void;
}

/**
 * Wires real DOM detectors for every trigger kind `proactive.ts` knows how
 * to match, scoped to the current `pagePath`. Each trigger fires
 * `onTriggerMatched` at most once per watcher instance — `CompanionWidget`
 * creates a fresh watcher on every page-context navigation (see
 * `pageContext.ts`'s `watchPageContext`), which naturally resets dwell
 * timers and re-arms scroll/exit-intent detection for the new page.
 */
export function watchProactiveTriggers(
  options: ProactiveTriggerWatcherOptions
): ProactiveTriggerWatcherHandle {
  const win = options.win ?? window;
  const doc = options.doc ?? document;
  const fired = new Set<CompanionProactiveTrigger>();

  const emit = (trigger: CompanionProactiveTrigger) => {
    if (fired.has(trigger)) {
      return;
    }
    fired.add(trigger);
    options.onTriggerMatched(trigger);
  };

  const dwellTimers: ReturnType<typeof setTimeout>[] = [];
  for (const trigger of options.triggers) {
    if (trigger.kind !== 'page_dwell') {
      continue;
    }
    const matched = matchProactiveTrigger([trigger], {
      pagePath: options.pagePath,
      dwellSeconds: trigger.dwellSeconds ?? 30
    });
    if (!matched) {
      continue;
    }
    const timer = setTimeout(() => emit(trigger), (trigger.dwellSeconds ?? 30) * 1000);
    dwellTimers.push(timer);
  }

  const handleScroll = () => {
    const scrollFraction = computeScrollFraction(
      win.scrollY,
      win.innerHeight,
      doc.documentElement.scrollHeight
    );
    for (const trigger of options.triggers) {
      if (trigger.kind !== 'scroll_depth' || fired.has(trigger)) {
        continue;
      }
      const matched = matchProactiveTrigger([trigger], { pagePath: options.pagePath, scrollFraction });
      if (matched) {
        emit(matched);
      }
    }
  };

  const handleMouseLeave = (event: MouseEvent) => {
    if (!isExitIntentClientY(event.clientY)) {
      return;
    }
    for (const trigger of options.triggers) {
      if (trigger.kind !== 'exit_intent' || fired.has(trigger)) {
        continue;
      }
      const matched = matchProactiveTrigger([trigger], { pagePath: options.pagePath });
      if (matched) {
        emit(matched);
      }
    }
  };

  const hasScrollTriggers = options.triggers.some((trigger) => trigger.kind === 'scroll_depth');
  const hasExitIntentTriggers = options.triggers.some((trigger) => trigger.kind === 'exit_intent');

  if (hasScrollTriggers) {
    win.addEventListener('scroll', handleScroll, { passive: true });
  }
  if (hasExitIntentTriggers) {
    doc.addEventListener('mouseleave', handleMouseLeave);
  }

  function stop(): void {
    for (const timer of dwellTimers) {
      clearTimeout(timer);
    }
    win.removeEventListener('scroll', handleScroll);
    doc.removeEventListener('mouseleave', handleMouseLeave);
  }

  return { stop };
}
